import { useState, type CSSProperties } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { CwInboxStage } from '@/api/inboxTypes';

interface FunnelOption {
  id: number;
  name: string;
}

interface Props {
  stages: CwInboxStage[];
  /** Непрочитанные по стадиям (CwUnreadCounts.stages); нет — числа скрыты. */
  counts?: Record<string, number> | null;
  selectedStageId: number | null;
  onSelect: (id: number | null) => void;
  /** Название канала, чья воронка показана. */
  funnelName: string;
  /** Каналы с воронкой — выбор показывается, если их больше одного и выбран режим «Все». */
  options?: FunnelOption[];
  onPickFunnel?: (inboxId: number) => void;
}

/**
 * «Полоса воронки» из прототипа: отрезки одинаковой ширины, по одному на стадию выбранного канала.
 * Число в отрезке — НЕПРОЧИТАННЫЕ переписки стадии (так считает сервер: надстройка unread_counts);
 * общее число переписок по стадиям Chatwoot одним запросом не отдаёт. Нажатие оставляет одну стадию.
 */
export function FunnelSpectrum({ stages, counts, selectedStageId, onSelect, funnelName, options, onPickFunnel }: Props) {
  const [menu, setMenu] = useState(false);
  if (stages.length === 0) return null;
  const picked = stages.find((s) => s.id === selectedStageId) ?? null;
  const total = counts ? stages.reduce((a, s) => a + (counts[String(s.id)] ?? 0), 0) : null;
  const canPick = !!options && options.length > 1 && !!onPickFunnel;

  return (
    <div className="spec">
      <div className="spec-cap">
        {picked ? (
          <>
            <span className="sdot" style={{ background: picked.color || 'var(--accent)' }} aria-hidden />
            <b>{picked.name}</b>
            {counts && <span className="tnum">{counts[String(picked.id)] ?? 0} непрочитанных</span>}
            <button type="button" className="clr" onClick={() => onSelect(null)}>Все стадии</button>
          </>
        ) : (
          <>
            <b className="truncate">{funnelName}</b>
            {total !== null && total > 0 && <span className="tnum">{total} непрочитанных</span>}
            {canPick && (
              <span className="pipebtn-wrap">
                <button type="button" className="pipebtn" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((v) => !v)}>
                  Воронка канала<ChevronDown aria-hidden />
                </button>
              </span>
            )}
          </>
        )}
      </div>
      {menu && canPick && (
        <>
          <div className="scrim" onClick={() => setMenu(false)} aria-hidden />
          <div className="menu under" role="menu">
            {options!.map((o) => (
              <button
                key={o.id}
                type="button"
                role="menuitemradio"
                aria-checked={o.name === funnelName}
                onClick={() => { onPickFunnel!(o.id); setMenu(false); }}
              >
                <Check aria-hidden />{o.name}
              </button>
            ))}
          </div>
        </>
      )}
      <div className={`bar${picked ? ' picked' : ''}`} role="group" aria-label={`Стадии воронки ${funnelName}`}>
        {stages.map((s) => {
          const n = counts?.[String(s.id)] ?? 0;
          return (
            <button
              key={s.id}
              type="button"
              className={`seg tnum${counts && n === 0 ? ' zero' : ''}`}
              style={{ '--c': s.color || 'var(--accent)' } as CSSProperties}
              aria-pressed={s.id === selectedStageId}
              aria-label={`${s.name}${counts ? `: непрочитанных ${n}` : ''}`}
              onClick={() => onSelect(s.id === selectedStageId ? null : s.id)}
            >
              {counts && n > 0 ? n : ''}
            </button>
          );
        })}
      </div>
      <div className="spec-names" aria-hidden>
        {stages.map((s) => <span key={s.id} className={s.id === selectedStageId ? 'on' : ''}>{s.name}</span>)}
      </div>
    </div>
  );
}
